<div class="table-container" [class.no-caption]="!hasCaption">
  <!-- Header with view buttons (not rendered when it would be empty) -->
  @if (hasCaption) {
  <div class="caption-container">
    <div class="header-row">
      @if (!pillFilters.length && pillFiltersLoading > 0) {
        <!-- Filters still loading (e.g. options from the API): skeleton pills, like the rows -->
        <div class="inline-filters-container pill-filters-skeleton">
          @for (i of [].constructor(pillFiltersLoading); track $index) {
            <p-skeleton width="110px" height="32px" borderRadius="999px"></p-skeleton>
          }
        </div>
      } @else if (pillFilters.length) {
        <app-inline-filters
          class="inline-filters-container"
          [filters]="pillFilters"
          [values]="pillFilterValues"
          (filterChange)="inlineFilterChange.emit($event)"
        ></app-inline-filters>
      }

      <div class="actions-container-top">

        <!-- ✅ Secondary Action Button (e.g., Validate All) -->
        @if (showSecondaryActionButton && (!isMobile || showSecondaryActionButtonOnMobile)) {
          <app-button
            [label]="secondaryActionButtonLabel || ''"
            [icon]="secondaryActionButtonIcon"
            [variant]="secondaryActionButtonVariant"
            size="xsmall"
            class="secondary-action-button"
            (onClick)="secondaryActionButtonClick.emit()"
          ></app-button>
        }

        <!-- ➕ Dynamic Add Button -->
        <!-- On desktop it is shown in the topbar (MenuService.pageAction$) -->
        @if (showActionButton && isMobile) {
          <button class="action-button" [attr.aria-label]="actionButtonLabel || ('common.actions.add' | transloco)" (click)="actionButtonMenuActions.length > 0 ? openActionButtonMenu($event.target) : actionButtonClick.emit()">
            <lucide-icon [name]="actionButtonIcon" [size]="20"></lucide-icon>
            <span class="action-button__label">{{ actionButtonLabel || ('common.actions.add' | transloco) }}</span>
          </button>
        }

        <!-- 🔍 Search Button (Mobile) -->
        @if (isMobile && showMobileSearch) {
          <button class="ellipsis-button" [attr.aria-label]="'common.labels.search' | transloco" (click)="toggleSearch()">
            <lucide-icon name="Search"></lucide-icon>
          </button>
        }

        <!-- 📌 Options Button (Mobile) -->
        @if (isMobile && showMobileMenu) {
          <button class="ellipsis-button" [attr.aria-label]="'common.a11y.moreOptions' | transloco" [attr.aria-expanded]="menuOpen" (click)="toggleMenu()">
            <lucide-icon name="EllipsisVertical"></lucide-icon>
          </button>

          <!-- 📌 Dropdown Menu -->
          @if (menuOpen) {
            <div class="action-menu">
              @if (showSelectCheckbox) {
                <button class="menu-item" (click)="toggleSelectionMode()">
                  <lucide-icon name="Squircle"></lucide-icon>
                  {{ isSelectionMode ? ('table.menu.cancelSelection' | transloco) : ('table.menu.select' | transloco) }}
                </button>
              }
              <button class="menu-item" (click)="triggerSortByMainField()">
                <lucide-icon name="ArrowDownUp"></lucide-icon> {{ 'table.menu.sort' | transloco }}
              </button>
              <button #filterBtnMobile class="menu-item" (click)="openFilter(filterBtnMobile)">
                <lucide-icon name="ListFilter"></lucide-icon> {{ 'table.menu.filter' | transloco }}
              </button>
            </div>
          }
        }

        <!-- 🔍 Search Bar -->
        @if (showSearch) {
          <div class="search-container" [class.expanded]="searchVisible">
            <button class="search-button" [attr.aria-label]="'common.labels.search' | transloco" [attr.aria-expanded]="searchVisible" (click)="toggleSearchOpen()">
              <lucide-icon name="Search" [size]="20"></lucide-icon>
              @if (!searchVisible) {
                <span class="search-button__label">{{ 'table.search.label' | transloco }}</span>
              }
            </button>
            @if (searchVisible) {
              <div class="search-input-wrapper">
                <app-input
                  [placeholder]="'table.search.placeholder' | transloco"
                  [(ngModel)]="searchTerm"
                  (keyup.enter)="onSearch()"
                  size="small"
                  >
                  <ng-template #suffix>
                    @if (searchTerm) {
                      <button class="search-clear-btn" [attr.aria-label]="'common.a11y.clearSearch' | transloco" (click)="clearSearch()">
                        <lucide-icon name="X" [size]="16"></lucide-icon>
                      </button>
                    }
                  </ng-template>
                </app-input>
              </div>
            }
          </div>
        }

        <!-- 📌 Filter Button (Desktop Only) -->
        @if (showFilter && (!isMobile || showFilterOnMobile)) {
          <button #filterBtn class="filter-button" (click)="openFilter(filterBtn)">
            <lucide-icon name="ListFilter" [size]="20"></lucide-icon>
            <span class="filter-button__label">{{ 'table.filterButton' | transloco }}</span>
          </button>
        }

        @if (showViewToggleType && !isMobile) {
          <!-- View buttons -->
          <div class="view-toggle">
            <button class="view-button" [class.active]="viewType === 'list'" [attr.aria-pressed]="viewType === 'list'" [attr.aria-label]="'common.a11y.listView' | transloco" (click)="viewType = 'list'">
              <lucide-icon name="ListCollapse"></lucide-icon>
            </button>
            <button class="view-button" [class.active]="viewType === 'card'" [attr.aria-pressed]="viewType === 'card'" [attr.aria-label]="'common.a11y.gridView' | transloco" (click)="viewType = 'card'">
              <lucide-icon name="LayoutGrid"></lucide-icon>
            </button>
          </div>
        }

        <!-- 📋 Selection Mode Button (Desktop Only) -->
        @if (!isMobile && showSelectCheckbox) {
          <div class="selection-button-wrapper">
            <button class="selection-toggle-button" [class.active]="isSelectionMode" [attr.aria-pressed]="isSelectionMode" [attr.aria-label]="(isSelectionMode ? 'table.menu.cancelSelection' : 'table.menu.select') | transloco" (click)="toggleSelectionMode()">
              <lucide-icon name="Squircle" [size]="20"></lucide-icon>
            </button>
            @if (isSelectionMode && selectedCount > 0) {
              <div class="selection-count-badge">
                {{ selectedCount }}
              </div>
            }
          </div>
        }

      </div>
    </div>

    <!-- 📌 Filter Chips -->
    @if (filterChips.length > 0) {
      <div class="filter-chips">
        @for (chip of filterChips; track chip.key) {
          <app-chip [label]="chip.label" [removable]="true" (onRemove)="onRemoveChip.emit(chip)" />
        }
      </div>
    }
  </div>
  }

  <!-- On mobile the next page loads under the list, without the skeleton -->
  @if (isLoading && !(isMobile && infiniteLoading)) {
    <!-- Skeleton is shown in specific views (list or table) -->
    @if (viewType === 'list') {
      <!-- 📱 Responsive Accordion View (Mobile) -->
      @if (isMobile) {
        <div class="accordion-list">
          @for (item of [1,2,3,4,5,6,7,8,9,10]; track item) {
            <div class="accordion-item-skeleton">
              <div class="accordion-item-header-skeleton">
                @if (hasAvatarColumn()) {
                  <p-skeleton shape="circle" size="40px"></p-skeleton>
                }
                <div class="skeleton-info-mobile">
                  <p-skeleton width="150px" height="16px" styleClass="mb-2"></p-skeleton>
                  <p-skeleton width="100px" height="14px"></p-skeleton>
                </div>
                @if (showOptionsButton || showNavigationButton) {
                  <p-skeleton shape="circle" size="32px"></p-skeleton>
                }
              </div>
            </div>
          }
        </div>
      } @else {
        <!-- 🖥️ Desktop Table View Skeleton -->
        <div class="table-skeleton-container">
          <table class="table-skeleton">
            @if (!hideHeader) {
              <thead>
                <tr>
                  @if (shouldShowCheckboxes) {
                    <th><p-skeleton shape="circle" size="20px"></p-skeleton></th>
                  }
                  @for (col of columns; track col.field) {
                    <th>
                      <p-skeleton width="100px" height="16px"></p-skeleton>
                    </th>
                  }
                  @if (showOptionsButton || showNavigationButton) {
                    <th></th>
                  }
                </tr>
              </thead>
            }
            <tbody>
              @for (item of [1,2,3,4,5,6,7,8,9,10]; track item) {
                <tr>
                  @if (shouldShowCheckboxes) {
                    <td><p-skeleton shape="circle" size="20px"></p-skeleton></td>
                  }
                  @for (col of columns; track col.field) {
                    <td>
                      @if (col.type === 'name' && col.showAvatar) {
                        <div style="display: flex; align-items: center; gap: 12px;">
                          <p-skeleton shape="circle" size="40px"></p-skeleton>
                          <p-skeleton width="120px" height="16px"></p-skeleton>
                        </div>
                      } @else {
                        <p-skeleton width="100px" height="16px"></p-skeleton>
                      }
                    </td>
                  }
                  @if (showOptionsButton || showNavigationButton) {
                    <td><p-skeleton shape="circle" size="32px"></p-skeleton></td>
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    } @else if (viewType === 'card') {
      <!-- Card View Skeleton -->
      <div class="p-d-flex p-flex-wrap p-gap-3 card-grid">
        @for (item of [1,2,3,4,5]; track item) {
          <div class="card-skeleton">
            <p-skeleton width="100%" height="200px" borderRadius="16px"></p-skeleton>
          </div>
        }
      </div>
    }
  } @else {
    <!-- List view -->
    @if (viewType === 'list') {
      <!-- 📱 Responsive Accordion View (Mobile) -->
      @if (isMobile) {
        <app-table-mobile-list
          [items]="mobileItems"
          [columns]="columns"
          [mainField]="mainField"
          [loadError]="loadError"
          [expandedIds]="expandedItems"
          [hasMore]="hasMoreMobileItems"
          [infiniteLoading]="infiniteLoading"
          [hideAccordionArrow]="hideAccordionArrow"
          [showMobileNavigateArrow]="showMobileNavigateArrow"
          [showMobileDownloadButton]="showMobileDownloadButton"
          [showMobileCheckboxes]="showMobileCheckboxes"
          [showOptionsButton]="showOptionsButton"
          [showNavigationButton]="showNavigationButton"
          [navigationButtonLabel]="navigationButtonLabel"
          [mobileSubtitleField]="mobileSubtitleField"
          [mobileSubtitleIcon]="mobileSubtitleIcon"
          [mobileSubtitleEmptyKey]="mobileSubtitleEmptyKey"
          [mobileAccordionFields]="mobileAccordionFields"
          [getMenuActions]="getMenuActions"
          (retry)="retryLoad.emit()"
          (rowSelect)="onRowSelect($event)"
          (toggle)="toggleItem($event)"
          (checkboxChange)="onMobileCheckboxChange($event)"
          (menuOpen)="openActionMenu($event.origin, $event.actions, $event.item)"
          (download)="onDownloadClick($event)"
          (navigate)="onNavigate($event)"
          (nearEnd)="loadNextMobilePage()">
        </app-table-mobile-list>
} @else {
  <!-- 🖥️ Desktop Table View -->
  <p-table #dt
    [value]="data"
    [(selection)]="selectedItems"
    dataKey="id"
    [lazy]="lazy"
    [rows]="rows"
    [totalRecords]="totalRecords"
    [globalFilterFields]="globalFilterFields"
    [responsiveLayout]="isMobile ? 'stack' : 'scroll'"
    [expandedRowKeys]="expandedRowKeys"
    [sortField]="sortField"
    [sortOrder]="sortOrder"
    (onSort)="onTableSort($event)"
    (onRowExpand)="onRowExpand($event)"
    (onRowCollapse)="onRowCollapse($event)"
    (onPage)="onPaginate($event)">

    <!-- 📭 Empty Message Template -->
    <ng-template pTemplate="emptymessage">
      <tr>
        <td [attr.colspan]="columns.length + (shouldShowCheckboxes ? 1 : 0) + (showOptionsButton || showNavigationButton ? 1 : 0)" class="empty-message">
          @if (loadError) {
            <div class="empty-state load-error" role="alert">
              <lucide-icon name="TriangleAlert" [size]="48"></lucide-icon>
              <p class="empty-title">{{ 'common.states.loadError' | transloco }}</p>
              <app-button
                [label]="'common.actions.retry' | transloco"
                icon="RefreshCw"
                variant="secondary"
                size="small"
                (onClick)="retryLoad.emit()">
              </app-button>
            </div>
          } @else {
            <div class="empty-state">
              <lucide-icon name="FileX" [size]="48"></lucide-icon>
              <p class="empty-title">{{ 'table.empty.title' | transloco }}</p>
              <p class="empty-subtitle">{{ 'table.empty.subtitle' | transloco }}</p>
            </div>
          }
        </td>
      </tr>
    </ng-template>

    @if (!hideHeader) {
      <!-- 📌 Table Header -->
      <ng-template pTemplate="header">
        <tr>
          <!-- ✅ Checkbox Header (Row Selection) -->
          @if (shouldShowCheckboxes) {
            <th>
              <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
            </th>
          }

          <!-- 🖥️ Desktop: Display All Columns -->
          @if (!isMobile) {
            @for (col of columns; track col.field) {
              @if (col.sortable !== false) {
                <th [pSortableColumn]="col.sortField || col.field">
                  <span class="th-inner">
                    @if (col.headerIcon) {
                      <lucide-icon [name]="col.headerIcon" [size]="16"></lucide-icon>
                    }
                    {{ col.header }}
                  </span>
                  <p-sortIcon [field]="col.sortField || col.field"></p-sortIcon>
                </th>
              } @else {
                <th>
                  <span class="th-inner">
                    @if (col.headerIcon) {
                      <lucide-icon [name]="col.headerIcon" [size]="16"></lucide-icon>
                    }
                    {{ col.header }}
                  </span>
                </th>
              }
            }
            <!-- ⚙️ Actions Column Header -->
            @if (showOptionsButton || showNavigationButton) {
              <th class="actions-header">
                <span class="th-inner"></span>
              </th>
            }
          }
        </tr>
      </ng-template>
    }

    <!-- 📌 Table Body Template -->
    <ng-template pTemplate="body" let-rowData>
      <!-- 🖥️ Desktop View: Display All Columns -->
      <tr [ngClass]="{ 'row-selected': selectedItems.includes(rowData), 'row-clicked': selectedRowForNavigation === rowData, 'row-error': rowData.status === 'error' }"
        (click)="onRowClick($event, rowData)">
        <!-- ✅ Checkbox for Selection -->
        @if (shouldShowCheckboxes) {
          <td>
            <p-tableCheckbox [value]="rowData"></p-tableCheckbox>
          </td>
        }

        <!-- 🔹 Loop through Columns and Render Data -->
        @for (col of columns; track col.field) {
          <td appTableCell [row]="rowData" [col]="col" [columns]="columns"></td>
        }

<!-- ⚙️ Actions Column -->
@if (showOptionsButton || showNavigationButton) {
  <td class="actions-column">
    <div class="actions-buttons">
      <!-- ⋮ Options Button -->
      @if (showOptionsButton && (getMenuActions?.(rowData) || []).length > 0) {
        <app-button
          class="ellipsis-button"
          [icon]="'ellipsis-vertical'"
          [ariaLabel]="'common.labels.actions' | transloco"
          variant="icon-ghost"
          size="xsmall"
          (onClick)="openActionMenu($event.target, (getMenuActions?.(rowData) || []), rowData)">
        </app-button>
      }

        <!-- ➡️ Arrow Right Button -->
        @if (showNavigationButton) {
          <app-button
            class="navigation-button"
            [icon]="navigationButtonIcon"
            [label]="(navigationButtonLabel | transloco) || ''"
            [variant]="navigationButtonLabel ? 'ghost' : 'icon-ghost'"
            [ariaLabel]="(navigationButtonLabel || 'common.actions.viewDetails') | transloco"
            size="xsmall"
            (onClick)="navigationTriggered.emit(rowData); $event.stopPropagation()">
          </app-button>
        }
    </div>
  </td>
}
</tr>
</ng-template>

</p-table>
}
}


@if (viewType === 'card') {
  <div class="p-d-flex p-flex-wrap p-gap-3 card-grid">
    @for (item of data; track item.id) {
      <div class="card-grid-item">
        <app-card
          [item]="item"
          [getMenuActions]="getMenuActions"
          (onMenuAction)="onMenuAction($event)">
        </app-card>
      </div>
    }
  </div>
}
}

@if (selectedItems.length > 0 && showActionButtonsDown) {
  <!-- 🔹 Action Bar (Extracted Component) -->
  <app-selection-action-bar
    [type]="actionBarType"
    [selectedCount]="selectedItems.length"
    [showEditButton]="selectedItems.length === 1"
    (onAction)="handleActionBarAction($event)">
  </app-selection-action-bar>
}

<!-- Pagination on desktop; on mobile the list loads more while scrolling -->
@if (showPaginator && totalRecords > rows && !isMobile) {
  <app-paginator
    [totalRecords]="totalRecords"
    [rows]="rows"
    (onPageChange)="onPaginate($event)"
  ></app-paginator>
}

</div>

<!-- 📱 Mobile Menu Modal (Bottom Sheet) -->
@if (isMobile && showMobileActionMenu) {
  <div class="mobile-menu-overlay" animate.leave="bt-sheet-leave" (click)="closeMobileActionMenu()">
    <div class="mobile-menu-container bt-sheet" role="dialog" aria-modal="true" [attr.aria-label]="title || mobileMenuEntityName" (click)="$event.stopPropagation()">
      <div class="mobile-menu-header">
        <span class="mobile-menu-title">{{ title }}</span>
        <button class="close-btn" [attr.aria-label]="'common.actions.close' | transloco" (click)="closeMobileActionMenu()">
          <lucide-icon name="X"></lucide-icon>
        </button>
      </div>
      <div class="mobile-menu-content">
        <span class="mobile-menu-subtitle">{{ mobileMenuEntityName }}</span>
        <div class="mobile-menu-actions">
          @for (action of mobileMenuActions; track action.id) {
            <button
              class="mobile-action-option"
              [class.red]="action.class === 'red'"
              (click)="action.command?.(); closeMobileActionMenu()">
              <span class="mobile-action-label">{{ action.label }}</span>
              <lucide-icon [name]="action.icon" class="mobile-action-icon"></lucide-icon>
            </button>
          }
        </div>
      </div>
    </div>
  </div>
}
